import { qs, qsa, on, delegate } from '../lib/dom.js';
import { AP_DATA } from '../data/listings.js';
import { renderListingsInto, renderListingsHTMLInto } from '../components/listing-card.js';
import { initFilterPanel, applyFilters, getFilterState, setSortParam, setPagedParam, getPagedParam, catalogUrlForPage, applyFacetCounts, setServerHeading, setModelFilter, setBrandFilter } from '../components/filter-panel.js';
import { paginate, renderPagination, bindPagination, PER_PAGE } from '../components/pagination.js';
import { initFinanceCalc } from '../components/finance-calc.js';
import { initScrollStrips, syncScrollStrips } from '../components/scroll-strip.js';
import { getFavs } from '../components/favorites.js';
import { initSavedSearch } from '../components/saved-search.js';
import { initSortDropdown } from '../components/sort-dropdown.js';
import { initRevealOnScroll } from '../components/entrance-animations.js';
import { refreshCollapsibleText } from '../components/collapsible-text.js';
import { beginCatalogLoading, endCatalogLoading } from '../lib/loading.js';
import { t } from '../lib/i18n.js';

// True when a WP REST endpoint is wired (set by wp_localize_script in the
// theme). When false the page falls back to phase-1 in-memory filtering.
const REST_BASE = (typeof window !== 'undefined' && window.AP_DATA && window.AP_DATA.apiBase) ? window.AP_DATA.apiBase : '';
const REST_NONCE = (typeof window !== 'undefined' && window.AP_DATA && window.AP_DATA.nonce) ? window.AP_DATA.nonce : '';

const SORTERS = {
  'relevance': (a, b) => 0,
  'mileage-asc': (a, b) => a.miles - b.miles,
  'mileage-desc': (a, b) => b.miles - a.miles,
  'price-asc': (a, b) => a.price - b.price,
  'price-desc': (a, b) => b.price - a.price,
  'year-asc': (a, b) => a.year - b.year,
  'year-desc': (a, b) => b.year - a.year,
};

// No local page size any more - it had drifted from the server's (15 vs 12) and
// produced a range label that did not match the cards on screen. The REST
// response carries the size it actually used; PER_PAGE is the fallback for the
// in-memory path, and is the same constant paginate() slices with.
const VIEW_STORAGE_KEY = 'autoplaza:view';

// Sort keys the UI/server both understand. 'relevance' is the default (server
// maps it to date-DESC) and is the only key NOT in SORTERS as a real comparator
// (it's a no-op), so REST is authoritative for it.
const VALID_SORTS = new Set([
  'relevance', 'mileage-asc', 'mileage-desc',
  'price-asc', 'price-desc', 'year-asc', 'year-desc',
]);

// Read & validate `?sort=` from the address bar. Returns null when absent or
// unknown so the caller can keep the default without a needless re-render.
function readSortFromUrl() {
  const raw = new URLSearchParams(window.location.search).get('sort');
  return raw && VALID_SORTS.has(raw) ? raw : null;
}

function loadView() {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    return v === 'list' || v === 'grid' ? v : 'grid';
  } catch { return 'grid'; }
}
function saveView(v) {
  try { localStorage.setItem(VIEW_STORAGE_KEY, v); } catch { }
}

let currentPage = 1;
let currentSort = 'relevance';
let currentView = loadView();

const BANNER_HTML = `
  <div class="ap-cat-banner">
    <div class="ap-cat-banner__copy">
      <span class="ap-eyebrow">${t('cat_banner_eyebrow', 'Leasing 0% avans · 5 minute')}</span>
      <div class="ap-cat-banner__title">${t('cat_banner_title', 'Pre-aprobare digitală - fără hârtii')}</div>
      <p class="ap-cat-banner__body">${t('cat_banner_body', 'Trimitem oferta de finanțare în 5 minute. Vinzi-ți și mașina veche prin platformă: o evaluăm gratuit.')}</p>
      <a href="#" class="ap-btn ap-btn--primary ap-btn--sm" style="align-self:flex-start;">
        ${t('cat_banner_cta', 'Pre-aprobare')}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
      </a>
    </div>
    <div class="ap-cat-banner__art" aria-hidden="true">
      <img src="img/leasing.webp" alt="" loading="lazy" decoding="async">
    </div>
  </div>
`;

function updateCounts(total) {
  qsa('[data-role="catalog-total-count"], [data-role="filter-count"], [data-role="filter-count-btn"], [data-role="result-count"]').forEach((el) => {
    el.textContent = total;
  });
}

// Build a query string from the current filter + sort + view + page state.
//
// Multi-value filters are sent as ONE param with comma-joined values
// (`?brand=Nissan,Renault`) - NOT as repeated `&brand=…&brand=…` params.
// WP_REST_Request reads repeated keys via PHP's $_GET, which collapses to
// the last value only; the comma form keeps all selections. The PHP
// `$multi()` helper in inc/rest-api.php explodes on comma on the way in.
function buildQueryParams() {
  const state = getFilterState();
  const params = new URLSearchParams();
  // getFilterState() returns multi-value filters as Arrays (not Sets), so
  // gate on `.length` - gating on `.size` here silently dropped every multi
  // filter from the REST URL and made AJAX always return the un-filtered
  // result set.
  const setMulti = (key, arr) => { if (arr && arr.length) params.set(key, arr.join(',')); };

  if (currentSort && currentSort !== 'relevance') params.set('sort', currentSort);
  if (currentPage > 1) params.set('paged', currentPage);
  if (state.mode && state.mode !== 'all') params.set('mode', state.mode);
  // Free-text header/catalog search. The REST /listings endpoint reads
  // `search` (falling back to `s`/`q`) and applies it as WP_Query `s`. Without
  // this the AJAX repaint dropped the term and returned the full catalog.
  if (state.q) params.set('search', state.q);
  setMulti('brand', state.brands);
  setMulti('model', state.models);
  setMulti('body', state.bodies);
  setMulti('fuel', state.fuels);
  setMulti('trans', state.trans);
  setMulti('drive', state.drives);
  setMulti('color', state.colors);
  setMulti('collection', state.collection);
  setMulti('options', state.options);
  setMulti('engine', state.engine);
  setMulti('seats', state.seats);
  setMulti('origin', state.origin);
  if (state.priceMin != null) params.set('priceMin', state.priceMin);
  if (state.priceMax != null) params.set('priceMax', state.priceMax);
  if (state.rateMin != null) params.set('rateMin', state.rateMin);
  if (state.rateMax != null) params.set('rateMax', state.rateMax);
  if (state.powerMin != null) params.set('powerMin', state.powerMin);
  if (state.powerMax != null) params.set('powerMax', state.powerMax);
  if (state.yearMin != null) params.set('yearMin', state.yearMin);
  if (state.yearMax != null) params.set('yearMax', state.yearMax);
  if (state.kmMin != null) params.set('kmMin', state.kmMin);
  if (state.kmMax != null) params.set('kmMax', state.kmMax);
  if (state.consumptionMax != null) params.set('consumptionMax', state.consumptionMax);
  setMulti('city', state.cities);
  if (currentView === 'list') params.set('view', 'list');
  // Language MUST travel with every catalog re-fetch. The REST endpoint runs on
  // the language-neutral /wp-json/ base, so pll_current_language() there reflects
  // the API URL, not the page the user is on; server-side atpl_rest_lang() then
  // has to fall back to the Referer, which is dropped by privacy settings, proxies
  // and (on subdirectory installs) a home_url() base-strip edge case - silently
  // returning RO on the RU catalog. AP_DATA.lang is server-authoritative for the
  // page, so send it explicitly and the endpoint never has to guess. This mirrors
  // saved-search / valuation / header-search, which already send `lang`.
  const lang = (AP_DATA && AP_DATA.lang) ? AP_DATA.lang : '';
  if (lang) params.set('lang', lang);
  return params;
}

// In-flight request token so out-of-order REST responses can be discarded.
let restToken = 0;

// Session-scoped response cache: toggling a filter back and forth (or
// revisiting a page number) replays the response instantly with zero
// network. The server flushes its own cache on inventory changes; a page
// reload clears this one, so within-session staleness is bounded and rare.
const REST_CACHE = new Map();
const REST_CACHE_MAX = 60;

async function rerenderViaRest() {
  const grid = qs('[data-role="catalog-results"]');
  if (!grid) return;
  grid.classList.add('is-loading');
  beginCatalogLoading();

  const params = buildQueryParams();
  // Piggyback facet counts on the same request - one round trip per filter
  // change instead of separate /listings + /filter-facets calls.
  params.set('facets', '1');
  // Also ask for the reactive bottom block (price cards + FAQ) so it tracks the
  // active filters. The server only returns it for page 1 (empty otherwise).
  params.set('bottom', '1');
  // …and the <head> meta for the new URL. Filters/sort/pagination rewrite the
  // URL with replaceState and never reload, so without this the canonical,
  // hreflang, og:* and description keep describing the first page loaded.
  params.set('head', '1');
  const cacheKey = params.toString();
  const token = ++restToken;
  const url = `${REST_BASE}listings?${cacheKey}`;

  let data = REST_CACHE.get(cacheKey);
  if (!data) {
    try {
      const res = await fetch(url, {
        headers: REST_NONCE ? { 'X-WP-Nonce': REST_NONCE } : {},
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`REST ${res.status}`);
      data = await res.json();
      REST_CACHE.set(cacheKey, data);
      if (REST_CACHE.size > REST_CACHE_MAX) {
        REST_CACHE.delete(REST_CACHE.keys().next().value);
      }
    } catch (err) {
      grid.classList.remove('is-loading');
      endCatalogLoading();
      console.error('[autoplaza] catalog REST failed', err);
      return;
    }
  }

  // Race protection - bail if a newer request has been kicked off.
  if (token !== restToken) { endCatalogLoading(); return; }
  grid.classList.remove('is-loading');
  endCatalogLoading();

  if (data && data.head) applyHead(data.head);

  if (typeof data.html === 'string') {
    if (data.total === 0 || data.html.trim() === '') {
      grid.innerHTML = `
        <div class="ap-catalog-empty">
          <h3>${t('cat_empty_title', 'Niciun rezultat pentru filtrele selectate.')}</h3>
          <p>${t('cat_empty_body', 'Încearcă să elimini câteva criterii sau salvează căutarea - te anunțăm când apare ceva nou.')}</p>
        </div>`;
    } else {
      renderListingsHTMLInto(grid, data.html);
    }
  }

  // Apply the view layout together with the freshly-rendered (view-correct)
  // cards, so the grid never shows the previous view's markup under the new
  // layout class.
  grid.classList.toggle('is-list', currentView === 'list');

  if (data.facets) applyFacetCounts(data.facets);

  // Sync the H1 to the server's SEO override (pure single-brand / brand+model
  // with a stored override) or clear it so the client template heading takes
  // over. Defaults to '' when the field is absent (older cache / no facets).
  setServerHeading(typeof data.heading === 'string' ? data.heading : '');

  // Swap the reactive bottom block (manufacturer/model price cards + FAQ) to
  // match the active filters. `data.bottom` is a string only for page 1; for
  // deeper pages the server sends '' so the block clears (matching a reload).
  if (typeof data.bottom === 'string') {
    const bottom = qs('[data-role="catalog-bottom"]');
    if (bottom) {
      bottom.innerHTML = data.bottom;
      // The price table and the SEO copy ship EXPANDED with their toggle
      // hidden (that is the markup a crawler must get) and are normally armed
      // by the one-shot first-scroll pass, which is long gone by the time a
      // filter runs. Without this they stayed fully expanded with no "show
      // more" button until the next full page load.
      refreshCollapsibleText(bottom);
    }
  }

  // Swap the single-brand MODELS quick-filter row to match the active brand.
  // The server sends it on every faceted request (a top nav that persists across
  // pages), '' when the selection isn't a single brand so the row clears.
  if (typeof data.models_row === 'string') {
    const mr = qs('[data-role="catalog-models-row"]');
    if (mr) {
      mr.innerHTML = data.models_row;
      // Fresh markup, fresh widths - re-evaluate whether the arrows are needed.
      syncScrollStrips(mr);
    }
  }

  const total = Number(data.total ?? 0);
  const pages = Number(data.pages ?? 1);
  const page = Number(data.page ?? currentPage);
  currentPage = page;

  // Range + total counts
  const size = Number(data.per_page) > 0 ? Number(data.per_page) : PER_PAGE;
  const start = total === 0 ? 0 : (page - 1) * size + 1;
  const end = Math.min(page * size, total);
  const rs = qs('[data-role="range-start"]'); if (rs) rs.textContent = start;
  const re = qs('[data-role="range-end"]'); if (re) re.textContent = end;
  const rt = qs('[data-role="range-total"]'); if (rt) rt.textContent = total;
  updateCounts(total);

  renderPagination(qs('[data-role="pagination"]'), page, pages, catalogUrlForPage);
  initRevealOnScroll(grid);
}

function rerenderInMemory() {
  const sorter = SORTERS[currentSort] || SORTERS.relevance;
  const filtered = applyFilters(AP_DATA.listings).slice().sort(sorter);
  const page = paginate(filtered, currentPage);
  currentPage = page.page;

  const grid = qs('[data-role="catalog-results"]');
  if (!grid) return;

  if (filtered.length === 0) {
    grid.innerHTML = `
      <div class="ap-catalog-empty">
        <h3>${t('cat_empty_title', 'Niciun rezultat pentru filtrele selectate.')}</h3>
        <p>${t('cat_empty_body', 'Încearcă să elimini câteva criterii sau salvează căutarea - te anunțăm când apare ceva nou.')}</p>
      </div>`;
  } else {
    const bannerAfter = currentView === 'list' ? 3 : 5;
    const banner = filtered.length > bannerAfter + 2
      ? { afterIndex: bannerAfter, html: BANNER_HTML }
      : null;
    renderListingsInto(grid, page.items, { favs: getFavs(), view: currentView, banner });
  }
  grid.classList.toggle('is-list', currentView === 'list');

  // Range + counts
  const start = filtered.length === 0 ? 0 : (page.page - 1) * PER_PAGE + 1;
  const end = Math.min(page.page * PER_PAGE, filtered.length);
  const rs = qs('[data-role="range-start"]'); if (rs) rs.textContent = start;
  const re = qs('[data-role="range-end"]'); if (re) re.textContent = end;
  const rt = qs('[data-role="range-total"]'); if (rt) rt.textContent = filtered.length;
  updateCounts(filtered.length);

  renderPagination(qs('[data-role="pagination"]'), page.page, page.totalPages, catalogUrlForPage);
  initRevealOnScroll(grid);
}

// Skip-first-paint flag for the WP path: the server already rendered the
// initial grid, so we let `filter:change` fire once without hitting REST
// (no filters applied yet = same as server render).
let initialRestRender = true;

/**
 * Put the document <head> back in step with the URL after a client-side change.
 *
 * The values are computed server-side by inc/catalog-head.php, which runs the
 * REAL head printer against the new filter context - so this only has to apply
 * them, never decide them.
 *
 * Absent/empty values REMOVE their tag rather than leaving the previous page's
 * value behind: a multi-value combo intentionally carries no canonical, and a
 * faceted URL carries no hreflang at all, so a stale tag there would be worse
 * than none. `robots` is the exception - '' means "the theme prints no tag of
 * its own here", which is core's business, so we leave whatever is there.
 */
function applyHead(head) {
  if (head.title) document.title = head.title;

  const meta = (selector, create, value) => {
    let el = document.head.querySelector(selector);
    if (!value) { if (el) el.remove(); return; }
    if (!el) { el = create(); document.head.appendChild(el); }
    el.setAttribute('content', value);
  };
  const named = (name, value) => meta(`meta[name="${name}"]`, () => {
    const m = document.createElement('meta'); m.setAttribute('name', name); return m;
  }, value);
  const prop = (property, value) => meta(`meta[property="${property}"]`, () => {
    const m = document.createElement('meta'); m.setAttribute('property', property); return m;
  }, value);

  named('description', head.description);
  prop('og:description', head.ogDescription || head.description);
  named('twitter:description', head.ogDescription || head.description);
  prop('og:title', head.ogTitle);
  named('twitter:title', head.ogTitle);
  prop('og:url', head.ogUrl);
  if (head.robots) named('robots', head.robots);

  // canonical: absent on the contexts that deliberately have none
  let link = document.head.querySelector('link[rel="canonical"]');
  if (head.canonical) {
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', head.canonical);
  } else if (link) {
    link.remove();
  }

  // hreflang: replace the whole set, so a faceted URL ends up with none
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((l) => l.remove());
  Object.keys(head.hreflang || {}).forEach((code) => {
    const l = document.createElement('link');
    l.setAttribute('rel', 'alternate');
    l.setAttribute('hreflang', code);
    l.setAttribute('href', head.hreflang[code]);
    document.head.appendChild(l);
  });
}

// Collapse a burst of filter changes into the one request that matters.
//
// The continuous controls - the price/year/power range sliders (price is
// step=500 across a 0-100000 track, so 200 stops) - fire an `input` event per
// step, and each distinct value is a distinct REST_CACHE key. One drag across
// the track therefore used to launch up to ~200 /listings round trips, of which
// `restToken` discarded every response but the last: pure load on the server and
// the visitor's connection, for one usable answer. It also made the endpoint
// impossible to rate-limit honestly, since a customer moving a slider looked
// exactly like a scraper.
//
// Only the NETWORK call waits. fireChange() still updates the chips, the heading
// and the URL hash synchronously, so dragging stays as responsive as before.
const REST_SETTLE_MS = 250;
let restSettleTimer = 0;

function rerender() {
  if (REST_BASE) {
    if (initialRestRender) {
      initialRestRender = false;
      // First call comes from initFilterPanel()'s fireChange() - the server
      // already painted matching results in GRID view. Skip the round-trip
      // unless the saved view is list (then we need the list-markup cards).
      if (currentView !== 'list') return;
    }
    // No begin/endCatalogLoading() here: that pair is a counter owned by
    // rerenderViaRest(), and arming a timer must not leave it unbalanced.
    clearTimeout(restSettleTimer);
    restSettleTimer = setTimeout(rerenderViaRest, REST_SETTLE_MS);
  } else {
    rerenderInMemory();
  }
}

// Keep the header/drawer language switcher pointed at the CURRENT filtered URL.
// The switcher <a> hrefs are rendered server-side at page load; once filters,
// sort or pagination rewrite the URL via replaceState, those hrefs go stale and
// switching language would reset every applied filter (the reported bug).
// Catalog URLs are canonical — identical slugs in both languages, only the
// `/ru/` prefix differs — so we mirror the live path + query onto each switcher
// link, swapping just the language prefix. Runs on the catalog page only.
function initCatalogLangSwitcher() {
  const links = qsa('.ap-header__lang a[hreflang], .ap-lang-switch__btn[hreflang]');
  if (!links.length) return;
  const sync = () => {
    const toRO = window.location.pathname.replace(/^\/ru(\/|$)/, '/');
    const rest = window.location.search + window.location.hash;
    const toRU = '/ru' + (toRO === '/' ? '/' : toRO);
    links.forEach((a) => {
      const lang = a.getAttribute('hreflang');
      if (lang === 'ru') a.setAttribute('href', toRU + rest);
      else if (lang === 'ro') a.setAttribute('href', toRO + rest);
    });
  };
  sync();
  // Every catalog URL update (filters/sort/pagination) flows through
  // pushState/replaceState — wrap them so the switcher re-syncs each time.
  ['pushState', 'replaceState'].forEach((m) => {
    const orig = history[m];
    if (typeof orig !== 'function' || orig.__apLangWrapped) return;
    const wrapped = function () {
      const r = orig.apply(this, arguments);
      sync();
      return r;
    };
    wrapped.__apLangWrapped = true;
    history[m] = wrapped;
  });
  window.addEventListener('popstate', sync);
}

export function initCatalogPage() {
  initCatalogLangSwitcher();
  initFilterPanel();
  // Pointer-device scrolling for the quick-filter strip (arrows + wheel).
  initScrollStrips();
  initFinanceCalc();
  initSavedSearch();

  // Adopt the page parsed from the URL path (/catalog/bmw/page/3/) so the client
  // stays in step with the server-rendered page instead of resetting to 1.
  currentPage = getPagedParam() || 1;

  bindPagination(qs('[data-role="pagination"]'), (p) => { currentPage = p; setPagedParam(p); rerender(); });
  on(document, 'filter:change', () => { currentPage = 1; setPagedParam(1); rerender(); });

  // Catalog quick-filter row. One strip, two payloads: MANUFACTURERS while the
  // selection isn't a single brand, that brand's MODELS once it is. Tapping a
  // pill REPLACES the corresponding selection (an empty attribute = "Toate" =
  // clear). The container is server-rendered on load and swapped wholesale from
  // the /listings response on every filter change, so both handlers delegate on
  // document to survive the swaps - and picking a brand swaps the strip over to
  // that brand's models on the very next response.
  //
  // setBrandFilter/setModelFilter fire `filter:change`, which re-renders and
  // rewrites the URL; the next server render re-confirms the active pill. The
  // feedback below is purely optimistic, for an instant response.
  const pillFeedback = (btn, selector, slug) => {
    const track = btn.parentElement;
    const row = btn.closest('[data-role="catalog-models-row"]') || document;
    qsa(selector, row).forEach((b) => b.classList.toggle('is-active', b === btn));
    // Matches the server re-render: the selected pill moves to 2nd position,
    // right after "Toate"; "Toate" itself stays first.
    if (slug && track) {
      const toate = track.querySelector(`${selector.slice(0, -1)}=""]`);
      if (toate && btn !== toate.nextElementSibling) {
        track.insertBefore(btn, toate.nextElementSibling);
      }
    }
    // Scroll back to the start so "Toate" + the now-2nd pill are in view.
    if (track && typeof track.scrollTo === 'function') {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    }
  };

  delegate(document, 'click', '[data-model-tab]', (e, btn) => {
    e.preventDefault();
    const slug = btn.dataset.modelTab || '';
    pillFeedback(btn, '[data-model-tab]', slug);
    setModelFilter(slug ? [slug] : []);
  });

  delegate(document, 'click', '[data-brand-tab]', (e, btn) => {
    e.preventDefault();
    const slug = btn.dataset.brandTab || '';
    pillFeedback(btn, '[data-brand-tab]', slug);
    setBrandFilter(slug ? [slug] : []);
  });

  // Sort dropdown. On load, adopt any valid `?sort=` from the URL as the
  // canonical state and reflect it in the select BEFORE first render so the
  // client matches the URL (and the server-rendered, already-sorted HTML).
  // The server pre-selects the matching <option> for `?sort=`, so in practice
  // the select already shows it - we sync defensively and keep currentSort in
  // step so a later filter change doesn't emit a stale sort.
  const sortSelect = qs('[data-role="sort-select"]');
  const urlSort = readSortFromUrl();
  if (urlSort) {
    currentSort = urlSort;
    if (sortSelect && sortSelect.value !== urlSort) sortSelect.value = urlSort;
  }
  // Keep filter-panel's canonical URL writer aware of the initial sort so a
  // subsequent filter change preserves (or correctly drops) the `?sort=` param.
  setSortParam(currentSort);

  if (sortSelect) on(sortSelect, 'change', () => {
    currentSort = sortSelect.value;
    currentPage = 1;
    setPagedParam(1);
    // Route the URL update through the SAME writer filters use (replaceState in
    // filter-panel) so sort + filters share one canonical URL - never two
    // competing history entries. Then re-render with the new sort applied.
    setSortParam(currentSort);
    rerender();
  });

  // Enhance the native sort <select> into the custom dropdown. Runs AFTER the
  // URL-sort adoption above so the trigger label reflects the resolved value,
  // and AFTER the change handler is bound so a pick re-renders as before (the
  // custom UI dispatches a native `change` on the same select).
  initSortDropdown();

  // Grid/list view toggle (persisted in localStorage)
  delegate(document, 'click', '.ap-cat-toolbar__view-btn', (e, btn) => {
    if (btn.dataset.view === currentView) return;
    qsa('.ap-cat-toolbar__view-btn').forEach((b) => b.classList.toggle('is-active', b === btn));
    currentView = btn.dataset.view;
    saveView(currentView);
    // Don't flip the grid's layout class yet - that would restyle the CURRENT
    // (other-view) cards mid-air. rerender() shows the preloader, fetches the
    // correctly-rendered cards, and applies `is-list` atomically with them.
    rerender();
  });

  // Reflect persisted view on first paint
  qsa('.ap-cat-toolbar__view-btn').forEach((b) => {
    b.classList.toggle('is-active', b.dataset.view === currentView);
  });
  // `is-list` is applied by the render paths atomically with the cards (so the
  // grid never shows one view's markup under the other's layout). For a saved
  // list view, rerender() below fetches the list-markup cards.
  rerender();
}
